import { useMemo, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import type { PropertyDetailsFragment } from '__generated__/athena/graphql';
import { buildCarouselPhotos } from 'components/marketplace/helpers/galleryPhotos';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import { MiniMapPropertySummary } from 'components/marketplace/property/PropertySection/MiniMapPropertySummary';
import { ShareModal } from 'components/marketplace/property/ShareModal';

interface MiniPropertyDetailPageProps {
  property: PropertyDetailsFragment;
  onClose: () => void;
}

export const MobileMiniPropertyDetailPage = ({
  property,
  onClose,
}: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => !prev);
  };

  const handleOnClick = () => {
    window.location.href = `/properties/${property?.slug}/aid_${property?.addressToken}`;
  };

  // Floor plans intentionally omitted here: the PhotoCarousel cover-crops
  // portrait plans. Tapping a photo navigates to the full PDP, where floor
  // plans are rendered uncropped in the gallery and Lightbox.
  const carouselPhotos = useMemo(
    () => buildCarouselPhotos(property),
    [property?.photos, property?.floorPlanPhotos],
  );

  return (
    <Col className="h-full w-full" key={`mobile-mini-pdp-${property?.addressToken}`}>
      <Box className="shrink-0 pb-6">
        <MapPhotoCarousel
          key={`mobile-carousel-${property?.addressToken}`}
          photos={carouselPhotos}
          onClose={onClose}
          onOpenShareModal={handleToggleShareModal}
          isRounded
          onPhotoClick={handleOnClick}
        />
      </Box>
      <Col data-mini-pdp-scroll className="min-h-0 flex-1 touch-pan-y gap-2.5 overflow-y-auto px-5">
        <MiniMapPropertySummary property={property} />
        <Button
          analyticsName="mobile-map-pdp-link"
          href={`/properties/${property?.slug}/aid_${property?.addressToken}`}
          variant="secondary"
          aria-label="View home"
          className="w-full"
        >
          View home
        </Button>
      </Col>
      <ShareModal property={property} isOpen={isShareModalOpen} onClose={handleToggleShareModal} />
    </Col>
  );
};
